<script>
import {
    modalState, showModal
} from '@app/store/modalStore.svelte.js';
import { fade, scale } from 'svelte/transition';
import { onMount } from 'svelte';
import * as m from "@app/paraglide/messages.js";

let currentSlide = $state(0);
let hide = $state(true);
let imagesLoaded = $state(false);

// Add some additional slides and incorporate a release notes slide
const slides = [
        {
            title: "Multi-Language Audio Dubbing",
            sub: "Turn captions into dubbed dialogue with AI voices",
            description: "With the Audio Description plugin, create an Audio Dubbing event group linked to your captions or translations, then assign character voices in the Voices panel. Speech can be fitted to each cue, mixed over a music-and-effects stem, and exported as audio or video from File → Export → Audio Dubbing.",
            image: './assets/img/dubbing.jpg'
        },
        {
            title: "New Transcription Providers",
            sub: "GPT Transcribe and Gemini 3.5 Transcribe",
            description: "Automatic Transcription now includes GPT Transcribe (OpenAI) and Gemini 3.5 Transcribe (Google), with Gemini 3.5 as the default. Choose either from AI Tools → Automatic Transcription — GPT includes speaker diarization, and Gemini covers 78 languages with word-level timing.",
            image: './assets/img/transcription.jpg'
        },
        {
            title: "Video Watermarking",
            sub: "Team identity overlay on the video player",
            description: "Team admins can enable a visual watermark from Options → Watermark. Overlay date and time, the viewer's email, IP address, or custom text for everyone on the team — it appears on the player only and is not burned into the media file or any export.",
            image: './assets/img/watermark.jpg'
        }
];

// Preload all images to avoid janky transitions
function preloadImages() {
    const imagePromises = slides.map(slide => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = slide.image;
            img.onload = () => resolve(slide.image);
            img.onerror = () => reject(new Error(`Failed to load image: ${slide.image}`));
        });
    });

    Promise.all(imagePromises)
        .then(() => {
            imagesLoaded = true;
        })
        .catch(error => console.error('Error preloading images:', error));
}

onMount(() => {
    preloadImages();
});

function closeModal() {
    if (hide) {
        localStorage.setItem('cc-hide-v50', 'true');
    } else {
        localStorage.setItem('cc-hide-v50', 'false');
    }

    showModal('welcome');
}

function nextSlide() {
    currentSlide = (currentSlide + 1) % slides.length;
}

function prevSlide() {
    currentSlide = (currentSlide - 1 + slides.length) % slides.length;
}

function goToSlide(index) {
    currentSlide = index;
}
</script>

<div class="modal fade {modalState.selected === 'tips' ? 'show d-block' : ''}" role="dialog" aria-labelledby="tipsTitle" aria-modal="true" tabindex="-1" id="TipsModal">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="tipsTitle">{m.whats_new()}</h4>
                <button type="button" class="btn-close" aria-label={m.close_tips()} onclick={() => closeModal()}></button>
            </div>
            <div class="modal-body">
                <div class="carousel-container">
                    <!-- Slide content -->
                    <div class="carousel-slide" role="region" aria-roledescription="carousel" aria-label={m.new_features_carousel()}>
                        {#key currentSlide}
                        <div class="slide-content" in:fade={{ duration: 300 }}>
                            <h3 class="slide-title">{slides[currentSlide].title}</h3>
                            <h5 class="slide-subtitle">{slides[currentSlide].sub}</h5>
                            
                            <div class="p-3">
                                <img 
                                    src={slides[currentSlide].image} 
                                    alt={m.feature_screenshot({ title: slides[currentSlide].title })} 
                                    class="img-fluid rounded shadow" 
                                    in:scale={{ duration: 200, delay: 100, start: 0.8 }}
                                />
                                
                                <!-- Hidden image preloading - only needed if Promise approach has issues -->
                                <div class="visually-hidden">
                                    {#each slides as slide (slide.image)}
                                        <img src={slide.image} alt={m.preload()} />
                                    {/each}
                                </div>
                            </div>
                            
                            <p class="slide-description">{slides[currentSlide].description}</p>
                        </div>
                        {/key}
                    </div>
                    
                    <!-- Navigation buttons -->
                    <button class="carousel-control prev" aria-label={m.previous_slide()} onclick={prevSlide}>
                        <span aria-hidden="true">&#10094;</span>
                    </button>
                    <button class="carousel-control next" aria-label={m.next_slide()} onclick={nextSlide}>
                        <span aria-hidden="true">&#10095;</span>
                    </button>
                    
                    <!-- Slide indicators -->
                    <div class="carousel-indicators">
                        {#each slides as slide, index (slide.image)}
                            <button 
                                class="indicator {index === currentSlide ? 'active' : ''}" 
                                aria-label={m.go_to_slide({ index: index + 1 })}
                                aria-current={index === currentSlide}
                                onclick={() => goToSlide(index)}
                            ></button>
                        {/each}
                    </div>
                </div>
            </div>
            <div class="p-3 border-top border-light">
                <div class="float-start">
                    <div class="form-check form-switch">
                        <input type="checkbox" class="form-check-input" id="hideCheck" bind:checked={hide}>
                        <label class="form-check-label" for="hideCheck">{m.hide_on_startup()}</label>
                    </div>
                </div>                
                <div class="float-end">                          
                    <button type="button" class="btn btn-primary me-2" id="closeTipsBtn" aria-label={m.close_tips()} onclick={() => closeModal()}>{m.close()}</button>
                    <a class="btn btn-outline-dark" href="https://www.closedcaptioncreator.com/releases.html" target="_blank" rel="noreferrer" aria-label={m.learn_more_updates()}>{m.learn_more()}</a>
                </div>                
            </div>
        </div>
    </div>
</div>

<style>
    .carousel-container {
        position: relative;
        overflow: hidden;
        padding: 20px 40px;
    }
    
    .carousel-slide {
        position: relative;
        width: 100%;
        min-height: 400px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    
    .slide-content {
        text-align: center;
        width: 100%;
    }
    
    .slide-title {
        font-weight: bold;
        margin-bottom: 0.5rem;
    }
    
    .slide-subtitle {
        color: #6c757d;
        margin-bottom: 1.5rem;
    }
    
    
    
    .slide-description {
        margin: 1rem auto;
        max-width: 80%;
    }
    
    .carousel-control {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        background-color: rgba(0, 0, 0, 0.3);
        color: white;
        border: none;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        cursor: pointer;
        transition: background-color 0.3s;
        z-index: 2;
    }
    
    .carousel-control:hover {
        background-color: rgba(0, 0, 0, 0.6);
    }
    
    .prev {
        left: 10px;
    }
    
    .next {
        right: 10px;
    }
    
    .carousel-indicators {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
        gap: 10px;
        margin-bottom: 10px;
    }
    
    .indicator {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background-color: #ccc;
        border: none;
        padding: 0;
        cursor: pointer;
        transition: background-color 0.3s;
    }
    
    .indicator.active {
        background-color: #007bff;
    }
</style>
